import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { getClientAsync, sendSurveyAsync } from '../../ajax/requests';
import { getErrorMessage } from '../../ajax/errors';
import { paths } from '../../app/constants';
import { FeatureEnum } from '../../models';
import { SurveySettingsData } from '../../shared/surveys';
import { alertError, alertSuccess } from '../../store/alerts/actions';
import { loadSurveys } from '../../store/settings/actions';
import { getSurveys, isSurveysLoading } from '../../store/settings/selectors';
import { getFeatures } from '../../store/session/selectors';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { ComboField } from '../Forms/fields';
import AppButton from '../AppButton';

const CREATE_FORM_OPTION_VALUE = '__create-form__';

interface ClientSurveyContact {
    email?: string;
    hasPhoneNumber: boolean;
}

export const canSendSurveyToClient = (
    survey: SurveySettingsData,
    contact: ClientSurveyContact,
): boolean => (survey.whatsAppEnabled ? contact.hasPhoneNumber : Boolean(contact.email));

export const getSurveyDisabledMessageKey = (
    survey: SurveySettingsData | undefined,
    surveys: SurveySettingsData[],
): TK | undefined => {
    if (survey) {
        return survey.whatsAppEnabled ? TK.clientPhoneRequiredForSurvey : TK.clientEmailRequiredForSurvey;
    }

    if (surveys.length === 0) {
        return undefined;
    }

    if (surveys.every(item => item.whatsAppEnabled)) {
        return TK.clientPhoneRequiredForSurvey;
    }

    if (surveys.every(item => !item.whatsAppEnabled)) {
        return TK.clientEmailRequiredForSurvey;
    }

    return TK.clientContactRequiredForSurvey;
};

export const getDefaultSurveyId = (
    surveys: SurveySettingsData[],
    contact: ClientSurveyContact,
    selectedSurveyId?: string,
): string | undefined => {
    const selectedSurvey = selectedSurveyId
        ? surveys.find(survey => survey.id === selectedSurveyId)
        : undefined;

    if (selectedSurvey) {
        return selectedSurvey.id;
    }

    return surveys.find(survey => canSendSurveyToClient(survey, contact))?.id ?? surveys[0]?.id;
};

export interface UseSendSurveyActionResult {
    canRenderAction: boolean;
    canSendSurvey: boolean;
    sendSurveyDisabledMessage?: string;
    selectedSurveyDisabledMessage?: string;
    selectedSurveyId: string | undefined;
    sendingSurvey: boolean;
    showSendSurveyModal: boolean;
    surveys: SurveySettingsData[];
    closeSendSurveyModal: () => void;
    openSendSurveyModal: () => void;
    redirectToCreateForm: () => void;
    sendSurvey: () => Promise<void>;
    setSelectedSurveyId: (surveyId: string | undefined) => void;
}

export interface SurveySentContext {
    surveyId: string;
    surveyName?: string;
    sentAt: string;
}

export const useSendSurveyAction = (
    clientId: string,
    appointmentId?: string,
    clientEmail?: string,
    onSurveySent?: (context?: SurveySentContext) => Promise<void> | void,
): UseSendSurveyActionResult => {
    const dispatch = useDispatch();
    const t = useTranslations();
    const features = useSelector(getFeatures);
    const surveys = useSelector(getSurveys) ?? [];
    const surveysLoading = useSelector(isSurveysLoading);

    const [selectedSurveyId, setSelectedSurveyId] = useState<string | undefined>(undefined);
    const [showSendSurveyModal, setShowSendSurveyModal] = useState(false);
    const [sendingSurvey, setSendingSurvey] = useState(false);
    const [contact, setContact] = useState<ClientSurveyContact>({
        email: clientEmail?.trim() || undefined,
        hasPhoneNumber: false,
    });

    useEffect(() => {
        if (!features.includes(FeatureEnum.Surveys)) {
            return;
        }

        if (!surveys.length && !surveysLoading) {
            dispatch(loadSurveys());
        }
    }, [dispatch, features, surveys.length, surveysLoading]);

    useEffect(() => {
        setSelectedSurveyId(current => getDefaultSurveyId(surveys, contact, current));
    }, [contact, surveys]);

    useEffect(() => {
        let cancelled = false;
        const normalizedClientEmail = clientEmail?.trim() || undefined;
        const shouldResolveClient = Boolean(clientId) && (!normalizedClientEmail || surveys.some(survey => survey.whatsAppEnabled));

        if (!clientId) {
            setContact({ email: normalizedClientEmail, hasPhoneNumber: false });
            return;
        }

        if (!shouldResolveClient) {
            setContact({ email: normalizedClientEmail, hasPhoneNumber: false });
            return;
        }

        getClientAsync(clientId)
            .then(client => {
                if (!cancelled) {
                    setContact({
                        email: normalizedClientEmail || client?.email?.trim() || undefined,
                        hasPhoneNumber: Boolean(client?.phoneNumbers?.some(phoneNumber => phoneNumber?.trim())),
                    });
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setContact({
                        email: normalizedClientEmail,
                        hasPhoneNumber: false,
                    });
                }
            });

        return () => {
            cancelled = true;
        };
    }, [clientEmail, clientId, surveys]);

    const selectedSurvey = useMemo(
        () => surveys.find(survey => survey.id === selectedSurveyId),
        [selectedSurveyId, surveys],
    );
    const canSendSurvey = useMemo(
        () => surveys.some(survey => canSendSurveyToClient(survey, contact)),
        [contact, surveys],
    );
    const canSendSelectedSurvey = selectedSurvey ? canSendSurveyToClient(selectedSurvey, contact) : false;
    const sendSurveyDisabledMessage = useMemo(() => {
        const key = getSurveyDisabledMessageKey(undefined, surveys);
        return !canSendSurvey && key ? t(key) : undefined;
    }, [canSendSurvey, surveys, t]);
    const selectedSurveyDisabledMessage = useMemo(() => {
        const key = getSurveyDisabledMessageKey(selectedSurvey, surveys);
        return !canSendSelectedSurvey && key ? t(key) : undefined;
    }, [canSendSelectedSurvey, selectedSurvey, surveys, t]);

    const closeSendSurveyModal = useCallback(() => {
        if (!sendingSurvey) {
            setSelectedSurveyId(undefined);
            setShowSendSurveyModal(false);
        }
    }, [sendingSurvey]);

    const openSendSurveyModal = useCallback(() => {
        if (surveys.length > 0) {
            setSelectedSurveyId(current => getDefaultSurveyId(surveys, contact, current));
            setShowSendSurveyModal(true);
        }
    }, [contact, surveys]);

    const redirectToCreateForm = useCallback(() => {
        setSelectedSurveyId(undefined);
        setShowSendSurveyModal(false);
        window.location.href = `${paths.settings()}/surveys`;
    }, []);

    const sendSurvey = useCallback(async () => {
        if (!selectedSurveyId || !canSendSelectedSurvey) {
            return;
        }

        setSendingSurvey(true);

        try {
            await sendSurveyAsync(selectedSurveyId, clientId, appointmentId);
            await onSurveySent?.({
                surveyId: selectedSurveyId,
                surveyName: selectedSurvey?.name,
                sentAt: new Date().toISOString(),
            });
            dispatch(alertSuccess(t(TK.sent)));
            setSelectedSurveyId(undefined);
            setShowSendSurveyModal(false);
        } catch (err: unknown) {
            dispatch(alertError(getErrorMessage(err) || t(TK.somethingWentWrong)));
        } finally {
            setSendingSurvey(false);
        }
    }, [appointmentId, canSendSelectedSurvey, clientId, dispatch, onSurveySent, selectedSurvey, selectedSurveyId, t]);

    return {
        canRenderAction: features.includes(FeatureEnum.Surveys) && surveys.length > 0,
        canSendSurvey,
        sendSurveyDisabledMessage,
        selectedSurveyDisabledMessage,
        selectedSurveyId,
        sendingSurvey,
        showSendSurveyModal,
        surveys,
        closeSendSurveyModal,
        openSendSurveyModal,
        redirectToCreateForm,
        sendSurvey,
        setSelectedSurveyId,
    };
};

interface SendSurveyModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreateForm: () => void;
    onSelectedSurveyIdChanged: (surveyId: string | undefined) => void;
    onSend: () => Promise<void>;
    selectedSurveyDisabledMessage?: string;
    selectedSurveyId: string | undefined;
    sendingSurvey: boolean;
    surveys: SurveySettingsData[];
}

export const SendSurveyModal: React.FC<SendSurveyModalProps> = ({
    isOpen,
    onClose,
    onCreateForm,
    onSelectedSurveyIdChanged,
    onSend,
    selectedSurveyDisabledMessage,
    selectedSurveyId,
    sendingSurvey,
    surveys,
}) => {
    const t = useTranslations();
    const options = useMemo(
        () => [
            ...surveys.map(survey => ({ value: survey.id, label: survey.name })),
            { value: CREATE_FORM_OPTION_VALUE, label: `+ ${t(TK.createForm)}` },
        ],
        [surveys, t],
    );

    const handleSurveyChange = useCallback((value: string | undefined) => {
        if (value === CREATE_FORM_OPTION_VALUE) {
            onCreateForm();
            return;
        }

        onSelectedSurveyIdChanged(value);
    }, [onCreateForm, onSelectedSurveyIdChanged]);

    return (
        <Modal isOpen={isOpen} toggle={onClose}>
            <ModalHeader toggle={onClose}>
                {t(TK.sendSurvey)}
            </ModalHeader>
            <ModalBody>
                <ComboField
                    label={t(TK.surveys)}
                    value={selectedSurveyId}
                    options={options}
                    onChange={handleSurveyChange}
                />
            </ModalBody>
            <ModalFooter>
                <AppButton color="secondary" onClick={onClose}>
                    {t(TK.cancel)}
                </AppButton>
                <AppButton
                    color="success"
                    onClick={onSend}
                    disabled={!selectedSurveyId || sendingSurvey || Boolean(selectedSurveyDisabledMessage)}
                    title={selectedSurveyDisabledMessage}
                >
                    {sendingSurvey ? t(TK.saving) : t(TK.send)}
                </AppButton>
            </ModalFooter>
        </Modal>
    );
};
